import { modelOptions, mongoose, PropType, plugin, post, prop, type Ref } from "@typegoose/typegoose";
import mongooseAutoPopulate from "mongoose-autopopulate";
import { z } from "zod";
import { ONLINE_STATUS } from "../../shared.js";
import { getZodSchemaFromClass, ObjectIdString, validate, valueRangeArray } from "../../zod-decorator/index.js";
import { getDisplayId } from "../commonGetters/getDisplayId.js";
import { getDisplayName } from "../commonGetters/getDisplayName.js";
import { ForeignLookup } from "../commonSubSchemas/foreignLookup.js";
import { DeviceStatistics } from "../device/subSchemas/deviceStatistics.js";
import { SoilMoistureEntry } from "../device/subSchemas/soilMoistureEntry.js";
import type { Organization } from "../organization/organization.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { SensoterraDeviceFieldsWithDefaults, SensoterraDeviceSchemaTypes } from "../types/sensoterraDevice.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";
import { Warning } from "../warning/warning.js";
import type { Zone } from "../zone/zone.js";
import { getSensorClusters } from "./getters.js";

export const SENSOTERRA_DEVICE_COLLECTION_NAME = "sensoterradevices" as const;
const SCHEMA_KEY = SENSOTERRA_DEVICE_COLLECTION_NAME;

@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        _id: true,
        collection: "sensoterradevices",
        timestamps: true,
    },
})
@plugin(mongooseAutoPopulate as any)
@post<SchemaBase<SensoterraDevice>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SensoterraDevice extends SchemaBase<SensoterraDevice, SensoterraDeviceFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public createdAt?: Date;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public updatedAt?: Date;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastSMSAlert?: Date;

    @validate({ childClass: Warning, childClassTransform: (object) => z.array(object).optional(), responseOnly: true })
    @prop({
        ref: () => Warning,
        localField: "node_id",
        foreignField: "foreignLookup.targetId",
        match: { "foreignLookup.targetCollection": "sensoterradevices", versionIsCurrent: true, deleted: false },
        autopopulate: true,
    })
    public warnings: Warning[] = [];

    @validate({ zodValidator: z.number() })
    @prop({ type: () => Number, required: true })
    public latitude!: number;

    @validate({ zodValidator: z.number() })
    @prop({ type: () => Number, required: true })
    public longitude!: number;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false, default: "" })
    public notes: string = "";

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public depth?: number;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public soilType?: string;

    /**
     * @deprecated
     * @type {number}
     */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 1.8 })
    public RunningAverageVoltage: number = 1.8;

    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 1.0 })
    public batteryPercentage: number = 1.0;

    @validate({ zodValidator: z.string().min(1), responseOnly: true })
    @prop({ type: () => String, required: true })
    public node_id!: string;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public name?: string;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public label?: string;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastPolledAt?: Date;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, ref: "Organization", required: true })
    public owner!: Ref<Organization>;

    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public sensoterraEmail?: string;

    @validate({ zodValidator: ObjectIdString.optional() })
    @prop({ type: () => mongoose.Types.ObjectId, ref: "Zone", required: false })
    public zone?: Ref<Zone>;

    @validate({ zodValidator: z.enum(["V1", "Seed"]).optional() })
    @prop({ type: () => String, required: false })
    public version?: "V1" | "Seed";

    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public EnclosureID!: string; // The serial number on the probe

    @validate({ childClass: DeviceStatistics, childClassTransform: (object) => object.optional(), responseOnly: true })
    @prop({
        _id: false,
        type: () => DeviceStatistics,
        required: true,
        default: new DeviceStatistics(),
    })
    public deviceStats: DeviceStatistics = new DeviceStatistics();

    @validate({ zodValidator: valueRangeArray.optional() })
    @prop({ type: () => Number, required: false }, PropType.ARRAY)
    public acceptableValueRange?: number[];

    @validate({
        childClass: SoilMoistureEntry,
        childClassTransform: (object) => z.array(object).optional(),
        responseOnly: true,
    })
    @prop({ type: () => SoilMoistureEntry, required: false }, PropType.ARRAY)
    public soilMoistureHistory?: SoilMoistureEntry[];

    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public batteryIndicator?: number;

    static getters = {
        getDisplayName,
        getDisplayId,
        getSensorClusters,
        getWarningContext: (self: SensoterraDevice) => ({
            foreignLookup: new ForeignLookup({
                targetId: self.node_id,
                targetForeignField: "node_id",
                targetCollection: SENSOTERRA_DEVICE_COLLECTION_NAME,
            }),
            targetInfo: {
                name: self.name ?? self.node_id,
                node_id: self.node_id,
                _id: self._id,
            },
            owner: self.owner,
        }),
    } as const satisfies Record<string, (self: SensoterraDevice) => any>;

    static utilities = {
        /**
         * Sensoterra devices are cloud-polled via Sensoterra's API and do not maintain a live connection.
         * Online status is therefore not meaningful for this device type.
         */
        getOnlineStatus: (_self: SensoterraDevice, _referenceTime?: Date): ONLINE_STATUS =>
            ONLINE_STATUS.LiveMonitoringNotSupported,
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SensoterraDeviceSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<SensoterraDevice, SensoterraDeviceFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

SensoterraDevice.dtoValidators = getZodSchemaFromClass(
    SensoterraDevice,
) as unknown as DtoValidatorTypes<SensoterraDeviceSchemaTypes>;
